const togglePasswordFieldView = (elem) => {
    const input = elem.previousElementSibling
    input?.setAttribute('type', input.getAttribute('type') === 'password' ? 'text' : 'password')
    elem.querySelectorAll('.toggle-icon').forEach(it => it.classList.toggle('hidden'))
    // Focus goes to the button on click, which now blurs the field and takes the requirements panel
    // down with it - so hand it straight back. Also just better: revealing what you typed should not
    // cost you the caret.
    input?.focus()
}
window.togglePasswordFieldView = togglePasswordFieldView // referenced by inline onclick= handlers in signup/reset-password markup

const authForm = document.getElementById('authForm')
const verificationSection = document.getElementById('verification')

// simple login form (login.ejs has no verification step): just disable the button and show the spinner
if (authForm && !verificationSection) {
    authForm.addEventListener('submit', () => {
        document.querySelector("button[type=submit]").disabled = true
        document.querySelector("svg.button-spinner").classList.remove("hidden")
    })
}

// signup / reset-password forms: password complexity validation, 6-box verification code
// entry, and an AJAX submit that reveals the email-code step instead of navigating away
if (authForm && verificationSection) {
    const password = document.getElementById("password")
    const passwordAssist = document.getElementById("passwordMessage")
    const letter = document.getElementById("letter")
    const capital = document.getElementById("capital")
    const number = document.getElementById("number")
    const special = document.getElementById("special")
    const length = document.getElementById("length")
    const signupIntro = document.getElementById("signupIntro")
    const signupFields = document.getElementById("signupFields")
    const signupFooter = document.getElementById("signupFooter")
    const verificationCodeField = document.getElementById("verificationCode")
    const codeBoxes = Array.from(document.querySelectorAll('[data-code-boxes] input'))
    // What each rule is called when we have to say it in a sentence rather than draw it.
    const RULE_NAMES = {
        letter: 'a lowercase letter',
        capital: 'a capital letter',
        number: 'a number',
        special: 'a special character',
        length: 'at least 8 characters',
    }

    /**
     * Marks each rule on the row and reports which are met.
     *
     * One place, because three callers need the same answer: the marks as she types, the blur
     * handler deciding whether it is safe to put the row away, and the submit guard. They were
     * three separate copies of the same condition, and the submit copy is the one that drifted.
     */
    const markPasswordRules = () => {
        const met = {
            letter: !!password.value.match(lowerCaseLetters),
            capital: !!password.value.match(upperCaseLetters),
            number: !!password.value.match(numbers),
            special: !!password.value.match(specialCharacters),
            length: password.value.length >= 8,
        }
        letter.setAttribute('data-check-valid', met.letter)
        capital.setAttribute('data-check-valid', met.capital)
        number.setAttribute('data-check-valid', met.number)
        special.setAttribute('data-check-valid', met.special)
        length.setAttribute('data-check-valid', met.length)
        return met
    }

    const passwordIsValid = () => Object.values(markPasswordRules()).every(Boolean)

    /** "a capital letter and a number", or "a number, a symbol and 8 characters". */
    const listMissing = (met) => {
        const missing = Object.keys(RULE_NAMES).filter(rule => !met[rule]).map(rule => RULE_NAMES[rule])
        if (missing.length <= 1) return missing.join('')
        return `${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]}`
    }

    const emailField = document.getElementById("email")
    const emailDisplay = document.getElementById("verificationEmailDisplay")
    const authSuccess = document.getElementById("authSuccess")
    const authButtonLabel = document.getElementById("authButtonLabel")
    const originalButtonLabel = authButtonLabel && authButtonLabel.innerText
    // The single submit button is shared between step 1 (signup/reset fields) and step 2
    // (verify code), which sit in different visual positions in each step's design -- move
    // the button node itself between them rather than keeping two buttons in sync.
    const authButton = document.getElementById("authButton")
    const authButtonHomeParent = authButton && authButton.parentNode
    const authButtonHomeNextSibling = authButton && authButton.nextSibling
    const verificationActions = document.getElementById("verificationActions")

    const lowerCaseLetters = /[a-z]/g
    const upperCaseLetters = /[A-Z]/g
    const numbers = /[0-9]/g
    const specialCharacters = /[@$!%*#?&-]/g

    const syncVerificationCode = () => {
        verificationCodeField.value = codeBoxes.map(box => box.value).join('')
    }

    const submitAuth = async (e, { isResend = false } = {}) => {
        e.preventDefault()
        // `invisible`, not `hidden` - the row reserves its space and toggles visibility. This line
        // still said `hidden` after that changed, so a weak password stopped the submit and showed
        // nothing at all: the button simply did not work.
        const met = markPasswordRules()
        if (!Object.values(met).every(Boolean)) {
            passwordAssist.classList.remove("invisible")
            // ...and say it again ABOVE THE BUTTON, which is where she is looking and the one place
            // a password manager's dropdown cannot cover. Chrome and Safari both overlay a "saved
            // password" panel directly under the field - exactly where the rule row sits - so
            // re-showing the row on its own can still leave her clicking a button that does nothing.
            const authError = document.getElementById("authError")
            authError.textContent = `Your password still needs ${listMissing(met)}.`
            authError.classList.remove("hidden")
            // Back to the field she has to fix. Focus also scrolls it into view, which matters on a
            // phone where the row can be below the fold.
            password.focus()
            return false
        }
        document.getElementById("authError").classList.add("hidden")

        document.querySelector("button[type=submit]").disabled = true
        document.querySelector("svg.button-spinner").classList.remove("hidden")

        if (password.getAttribute('type') !== 'password') togglePasswordFieldView(password.nextElementSibling)
        const formData = new FormData(authForm)
        const response = await fetch(authForm.action, {
            method: 'POST',
            body: formData
        })
        if (response.ok) {
            if (response.redirected) {
                // The verification-code confirm step succeeds via a server-side
                // redirect (to /results or /dashboard), not a JSON body -- fetch
                // follows that internally, so the browser itself never actually
                // navigates on its own unless told to.
                window.location.href = response.url
                return
            }
            if (authButtonLabel) authButtonLabel.innerText = 'Confirm'
            holdResend()
            // Clears a stale error from a previous failed attempt -- this
            // branch also runs on a successful "Resend the code", which
            // otherwise left the old "that's not quite right" message
            // showing even though a fresh code just went out.
            document.getElementById("authError").classList.add("hidden")
            if (isResend) {
                authSuccess.textContent = 'A new code is on its way.'
                authSuccess.classList.remove("hidden")
            } else {
                authSuccess.classList.add("hidden")
            }
            // Hide (not just grey out) the rest of the form on the verify step.
            signupIntro?.classList.add('hidden')
            signupFields?.classList.add('hidden')
            signupFooter?.classList.add('hidden')
            if (emailDisplay && emailField) emailDisplay.textContent = emailField.value
            if (authButton) verificationSection.insertBefore(authButton, verificationActions || null)
            verificationSection.classList.remove("hidden")
            verificationCodeField.required = true
            // On the boxes, not just the hidden field above: a type=hidden input is barred from
            // constraint validation, so that line alone never blocked anything.
            codeBoxes.forEach(box => { box.required = true })
            codeBoxes[0]?.focus()
        } else {
            // The routes answer a rejected signup with a short HTML fragment, and that is what gets
            // shown. But anything thrown BEFORE a route handles it - a schema parse, say - reaches
            // app.ts's error handler instead, which renders the whole error.ejs page. Putting that
            // through innerHTML injected an entire document, top bar and footer and all, into a div
            // inside the form: the signup page appeared to grow a second page below the password
            // field. So: use the body only when it is a fragment, and fall back otherwise.
            const body = (await response.text()).trim()
            const isWholePage = /^<!doctype|^<html/i.test(body)
            // And not a JSON body either. One route answered with {"message":"..."} and it went
            // on screen verbatim, braces and quotes and all. The guard above only knew about
            // whole pages, so anything that was not a readable fragment got printed as one.
            const isJson = /^[[{]/.test(body)
            const unusable = isWholePage || isJson
            document.getElementById("authError").innerHTML = (!unusable && body)
                || `Something went wrong. If the problem persists please contact us at <a href='mailto:hello@welleness.ai'>hello@welleness.ai</a>`
            if (unusable) console.error('Auth failed with a body that is not a message', response.status, body.slice(0, 120))
            document.getElementById("authError").classList.remove("hidden")
            authSuccess.classList.add("hidden")
        }
        document.querySelector("button[type=submit]").disabled = false
        document.querySelector("svg.button-spinner").classList.add("hidden")
    }

    // Shown on focus, hidden again on blur.
    //
    // The blur half was removed once because the panel sat in the form's flex column: collapsing it
    // out of the flow yanked everything below it - the submit button included - upward, and since
    // blur fires on MOUSEDOWN the button moved before the click completed and the click landed on
    // empty space. The row reserves its space now (see partials/password-help), so showing and
    // hiding it moves nothing and the blur handler is safe again.
    // `invisible`, not `hidden`: the row keeps its height either way, so nothing on the page moves
    // when it appears or goes. That is what makes the blur handler safe - blur fires on mousedown,
    // and the reason this used to have no blur handler at all was the submit button jumping upward
    // before the click landed.
    password.addEventListener('focus', () => { passwordAssist.classList.remove("invisible") })
    // ...but NOT while the password is still wrong. Nothing else on the page says why the button
    // will refuse - there is no `pattern` on the field, so the browser's own validation only catches
    // an EMPTY one - and hiding the row on the way out left her clicking a button that silently did
    // nothing. Empty is the exception: she has not attempted anything yet, and five red crosses for
    // a field she never typed in is nagging rather than help.
    password.addEventListener('blur', () => {
        if (!password.value || passwordIsValid()) passwordAssist.classList.add("invisible")
    })
    password.addEventListener('keyup', markPasswordRules)

    // 6-box verification code: auto-advance on input, backspace-to-previous when empty,
    // and paste support (pasting the full code from the email fills all 6 boxes).
    codeBoxes.forEach((box, idx) => {
        box.addEventListener('input', () => {
            box.value = box.value.replace(/\D/g, '').slice(-1)
            syncVerificationCode()
            if (box.value && codeBoxes[idx + 1]) codeBoxes[idx + 1].focus()
        })
        box.addEventListener('keydown', (e) => {
            if (e.key === 'Backspace' && !box.value && codeBoxes[idx - 1]) {
                codeBoxes[idx - 1].focus()
            }
        })
        box.addEventListener('paste', (e) => {
            e.preventDefault()
            const digits = (e.clipboardData || window.clipboardData).getData('text').replace(/\D/g, '').split('')
            codeBoxes.forEach((b, i) => { b.value = digits[i] || '' })
            syncVerificationCode()
            const lastFilled = Math.min(digits.length, codeBoxes.length) - 1
            codeBoxes[Math.max(lastFilled, 0)]?.focus()
        })
    })

    // The API sends nothing to the same address twice inside a minute, and still answers success so
    // it cannot be used to probe. Without this the button said a code was on its way when none was.
    const RESEND_SECONDS = 60
    const newCodeButton = document.getElementById('newCodeButton')
    const holdTimers = new Map()
    // Disabled with a countdown on its own label, so a held button says why it cannot be pressed yet.
    function hold(button) {
        if (!button) return
        const label = button.dataset.label || (button.dataset.label = button.textContent)
        let left = RESEND_SECONDS
        const show = () => { button.textContent = `${label} in ${left}s` }
        clearInterval(holdTimers.get(button))
        button.disabled = true
        show()
        holdTimers.set(button, setInterval(() => {
            left -= 1
            if (left > 0) return show()
            clearInterval(holdTimers.get(button))
            button.disabled = false
            button.textContent = label
        }, 1000))
    }
    function holdResend() { hold(newCodeButton) }

    // Text instead: a second live code, to her number, alongside the emailed one. Either confirms.
    // The screen has three states - email, asking for a text, text sent - each with its own heading in
    // the template (data-code-mode). While she is asking, the code entry stands aside: there is nothing
    // to type until the text arrives.
    const smsStatus = document.getElementById('smsStatus')
    const mobileField = document.getElementById('mobile')
    const mobileConsent = authForm.querySelector('input[name="mobileConsent"]')
    const codeEntry = [document.querySelector('[data-code-boxes]'), authButton]
    const emailControls = [document.getElementById('codeHelp'), verificationActions]

    const showCodeMode = (mode) => {
        document.querySelectorAll('[data-code-mode]').forEach(el => el.classList.toggle('hidden', el.dataset.codeMode !== mode))
        emailControls.forEach(el => el?.classList.toggle('hidden', mode !== 'email'))
        codeEntry.forEach(el => el?.classList.toggle('hidden', mode === 'sms-ask'))
        smsStatus?.classList.add('hidden')
        if (mode === 'sms-ask') mobileField?.focus()
        if (mode === 'sms-sent') codeBoxes[0]?.focus()
    }
    document.getElementById('smsToggle')?.addEventListener('click', () => showCodeMode('sms-ask'))
    document.getElementById('smsAgain')?.addEventListener('click', () => showCodeMode('sms-ask'))
    document.querySelectorAll('[data-back-to-email]').forEach(el => el.addEventListener('click', () => showCodeMode('email')))

    const sayProblem = (text) => {
        smsStatus.textContent = text
        smsStatus.classList.remove('hidden', 'text-welleness-teal')
        smsStatus.classList.add('text-welleness-coral')
    }

    const sendByText = async (e) => {
        const button = e.currentTarget
        // No tick, no text: the box is her permission to be texted, so it gates the send itself.
        if (mobileConsent && !mobileConsent.checked) {
            sayProblem('Please consent to us sending you account messages via text message.')
            mobileConsent.focus()
            return
        }
        button.disabled = true
        const response = await fetch(`${authForm.action}/sms`, {method: 'POST', body: new FormData(authForm)})
        const body = (await response.text()).trim()
        if (!response.ok) {
            sayProblem(/^<!doctype|^<html/i.test(body) || !body ? "We couldn't send that text. Please try again." : body)
            button.disabled = false
            return
        }
        hold(button)
        hold(document.getElementById('smsAgain'))
        const smsNumberDisplay = document.getElementById('smsNumberDisplay')
        if (smsNumberDisplay) {
            smsNumberDisplay.textContent = `+44 (0) ${mobileField.value.trim().replace(/^(\+44\s*|0)/, '')}`
            showCodeMode('sms-sent')
        } else {
            // The reset page: no states to move between, so the answer is said where she pressed.
            smsStatus.textContent = body
            smsStatus.classList.remove('hidden', 'text-welleness-coral')
            smsStatus.classList.add('text-welleness-teal')
        }
    }
    document.getElementById('smsSendButton')?.addEventListener('click', sendByText)
    document.getElementById('smsResetButton')?.addEventListener('click', sendByText)

    newCodeButton?.addEventListener('click', (e) => {
        e.preventDefault() // was previously missing -- the button is type=submit, so without
                            // this the browser's own default form submission would race the
                            // AJAX call below and navigate the page away.
        verificationCodeField.required = false
        // Clear whatever was typed (including a just-rejected wrong/expired
        // code) before resubmitting -- otherwise this resubmits with a
        // truthy verificationCode still in the hidden field, which the
        // server reads as "confirm with this code" rather than "send a new
        // one", so it just fails the same stale code again.
        verificationCodeField.value = ''
        // required cleared too -- a resend deliberately submits with no code.
        codeBoxes.forEach(box => { box.value = ''; box.required = false })
        submitAuth(new Event('submit'), { isResend: true })
    })

    document.getElementById('changeEmailButton')?.addEventListener('click', () => {
        showCodeMode('email')
        signupIntro?.classList.remove('hidden')
        signupFields?.classList.remove('hidden')
        signupFooter?.classList.remove('hidden')
        verificationSection.classList.add('hidden')
        verificationCodeField.required = false
        verificationCodeField.value = ''
        // ...and required, or the browser refuses to submit step 1 over a required box it is no longer rendering.
        codeBoxes.forEach(box => { box.value = ''; box.required = false })
        document.getElementById("authError").classList.add("hidden")
        authSuccess.classList.add("hidden")
        if (authButtonLabel && originalButtonLabel) authButtonLabel.innerText = originalButtonLabel
        if (authButton && authButtonHomeParent) authButtonHomeParent.insertBefore(authButton, authButtonHomeNextSibling)
        authForm.addEventListener('submit', submitAuth)
        emailField?.focus()
    })

    authForm.addEventListener('submit', submitAuth)
}
